// PrintButton — the standard print affordance for printable pages.
//
// Two modes:
//   - Browser print only: renders a single "Print" Button.
//   - Browser print + PDF download: renders a ButtonGroup with Print on
//     the left and a "↓ PDF" Button on the right. The PDF button shows
//     a spinner + disables itself while isLoadingPdf is true.
//
// The PDF download itself lives in usePdfDownload — this component does
// not own the request; the caller passes onDownloadPdf so any page can
// wire its own resource-specific endpoint and filename.

import { Download, Printer } from 'lucide-react';
import { Button } from '../Button/Button';
import { ButtonGroup } from '../ButtonGroup/ButtonGroup';

export interface PrintButtonProps {
  /** Defaults to window.print(). */
  onPrint?: () => void;
  /** When omitted, only the browser-print button is rendered. */
  onDownloadPdf?: () => void;
  isLoadingPdf?: boolean;
  /** Label for the print button. Defaults to "Print". */
  label?: string;
  /** Optional aria-label for the PDF button when icon-only. */
  pdfLabel?: string;
}

export function PrintButton({
  onPrint,
  onDownloadPdf,
  isLoadingPdf = false,
  label = 'Print',
  pdfLabel = 'Download PDF',
}: PrintButtonProps): JSX.Element {
  const handlePrint = onPrint ?? (() => window.print());

  if (!onDownloadPdf) {
    return (
      <Button variant="secondary" icon={Printer} onClick={handlePrint}>
        {label}
      </Button>
    );
  }

  return (
    <ButtonGroup>
      <Button variant="secondary" icon={Printer} onClick={handlePrint}>
        {label}
      </Button>
      <Button
        variant="secondary"
        icon={Download}
        onClick={onDownloadPdf}
        loading={isLoadingPdf}
        aria-label={pdfLabel}
      >
        PDF
      </Button>
    </ButtonGroup>
  );
}
